import * as Sentry from "@sentry/react";
import { getAuth } from "firebase/auth";
import { createCapture } from "../data/captureFault.js";

/**
 * Attribution for the callable SDK's one opaque failure (GH#566).
 *
 * `httpsCallable` collapses every transport-level failure into
 * `FirebaseError: internal` with no status, no headers, no body. The SDK is
 * explicit about why (@firebase/functions, inside `call()`):
 *
 *   "This could be an unhandled error on the backend, or it could be a network
 *    error. There's no way to know, since an unhandled error on the backend
 *    will fail to set the proper CORS header, and thus will be treated as a
 *    network error by fetch."
 *
 * A rejected preflight leaves no server-side log either, so today the only
 * artefact of the GH#566 failure is the word "internal". This module recovers
 * what the SDK threw away and emits ONE structured Sentry event per failed
 * call, so the next occurrence is attributable without a repro.
 *
 * Three observers, because each is blind where the others see:
 *
 *  1. A `fetch` observer, scoped to the callable URLs we actually build. Gets
 *     the HTTP status/headers/body when a response comes back, and the
 *     `TypeError` when the browser refuses to hand one over.
 *  2. `PerformanceResourceTiming`. Sees the wire even when CORS hides the
 *     response object: whether a request left at all, whether bytes came back,
 *     how long the preflight took, and (Chromium) the real `responseStatus`.
 *     This is what separates "the server answered and the browser blocked the
 *     answer" from "nothing reached the network".
 *  3. A probe: one replay of the same request via direct `fetch`, gated to
 *     read-only callables. Answers what the other two cannot — is the endpoint
 *     STILL failing seconds later (a persistent allowlist gap) or was it a
 *     one-shot deploy race?
 *
 * Everything here is best-effort and non-throwing: diagnostics must never turn
 * a callable failure into a different callable failure.
 *
 * PII: ids only. The request payload is never attached — only its top-level key
 * names and `orgId`.
 */

/** Cap on events per page load, so one broken screen cannot flood Sentry. */
const MAX_EVENTS_PER_PAGE = 10;

/**
 * Callables the probe may re-invoke.
 *
 * A probe re-runs the function for real, so this list holds only reads:
 * `unifiedReports` is a pure query, and `checkAndUpdateFreeTrialStatus` is an
 * idempotent upsert of a trial window that re-running cannot move. Anything
 * that writes ledger, billing or integration state stays out — no diagnostic is
 * worth a duplicated side effect. These are also exactly the two functions
 * GH#566 observed failing.
 */
const PROBE_ALLOWLIST = new Set([
  "unifiedReports",
  "checkAndUpdateFreeTrialStatus",
]);

const PROBE_TIMEOUT_MS = 5000;
const BODY_SNIPPET_CHARS = 300;

/**
 * Response headers worth recording. A cross-origin response only exposes the
 * CORS-safelisted set unless the server adds `Access-Control-Expose-Headers`,
 * so `access-control-allow-origin` — the header this bug is about — is NOT
 * readable from JS. We record what is readable plus the full readable-name
 * list, which is itself the signal: an empty list means an opaque response.
 */
const HEADERS_OF_INTEREST = [
  "content-type",
  "content-length",
  "cache-control",
  "expires",
  "last-modified",
  "x-cloud-trace-context",
  "x-vercel-id",
  "server",
];

/** URLs the fetch observer should record. Populated by `watchCallableUrl`. */
const watchedUrls = new Set();

/** url -> last observed fetch outcome. Bounded by `watchedUrls`. */
const fetchOutcomes = new Map();

let observerInstalled = false;
let probeUsed = false;
let eventsEmitted = 0;

/**
 * The same scope shape the data layer and the report Functions use, rather than
 * a fifth hand-rolled `withScope` body — see `src/data/captureFault.js`.
 */
const captureCallableFault = createCapture(Sentry, {
  defaultComponent: "callable",
  unknownFault: "opaque callable failure",
});

/**
 * The transport-opaque signature, and nothing else.
 *
 * A callable that reaches the server and throws `HttpsError("internal", msg)`
 * also arrives as `functions/internal`, but carries the server's message and
 * usually `details`. Only the SDK's own `_errorForResponse(0, null)` — the
 * network/CORS path — produces the literal message "internal" with no details.
 * Narrowing to that keeps the probe and the Sentry event off ordinary
 * server-side errors, which are already attributable from function logs.
 */
export function isOpaqueInternalError(error) {
  return Boolean(
    error &&
      error.code === "functions/internal" &&
      error.message === "internal" &&
      error.details === undefined,
  );
}

/** The URL the SDK will POST to, derived the way `Functions._url` derives it. */
export function callableUrl(functionsInstance, name) {
  try {
    const projectId = functionsInstance?.app?.options?.projectId;
    const region = functionsInstance?.region || "us-central1";
    if (functionsInstance?.emulatorOrigin) {
      return `${functionsInstance.emulatorOrigin}/${projectId}/${region}/${name}`;
    }
    if (functionsInstance?.customDomain) {
      return `${functionsInstance.customDomain}/${name}`;
    }
    if (!projectId) return null;
    return `https://${region}-${projectId}.cloudfunctions.net/${name}`;
  } catch {
    return null;
  }
}

export function watchCallableUrl(url) {
  if (url) watchedUrls.add(url);
}

function now() {
  return globalThis.performance?.now?.() ?? Date.now();
}

function truncate(value, max) {
  const text = typeof value === "string" ? value : String(value ?? "");
  return text.length > max ? `${text.slice(0, max)}…[truncated]` : text;
}

function readableHeaders(headers) {
  const values = {};
  const readableNames = [];
  try {
    for (const name of headers.keys()) readableNames.push(name);
    for (const name of HEADERS_OF_INTEREST) {
      const value = headers.get(name);
      if (value != null) values[name] = truncate(value, 120);
    }
  } catch {
    /* opaque response — nothing readable */
  }
  return { values, readableNames };
}

async function summarizeResponse(response, startedAt) {
  const summary = {
    transport: "response",
    status: response.status,
    statusText: response.statusText || null,
    responseType: response.type,
    redirected: response.redirected === true,
    durationMs: Math.round(now() - startedAt),
    headers: readableHeaders(response.headers),
  };
  // Only non-2xx bodies are read. A successful report response can be
  // megabytes, and cloning it to snapshot text nobody will look at is a real
  // memory cost on the happy path.
  if (!response.ok) {
    try {
      summary.bodySnippet = truncate(
        await response.clone().text(),
        BODY_SNIPPET_CHARS,
      );
    } catch {
      summary.bodySnippet = null;
    }
  }
  return summary;
}

/**
 * Wraps `globalThis.fetch` once. The hot path is a Set lookup on the request
 * URL, so every fetch that is not a callable we built passes straight through.
 */
export function installCallableFetchObserver(target = globalThis) {
  if (observerInstalled) return false;
  if (typeof target?.fetch !== "function") return false;

  const original = target.fetch;
  const observed = async function observedFetch(input, init) {
    let url = null;
    try {
      url = typeof input === "string" ? input : (input?.url ?? null);
    } catch {
      url = null;
    }
    if (!url || !watchedUrls.has(url)) return original.call(this, input, init);

    const startedAt = now();
    try {
      const response = await original.call(this, input, init);
      fetchOutcomes.set(url, await summarizeResponse(response, startedAt));
      return response;
    } catch (error) {
      // A CORS rejection surfaces here as an unhelpful `TypeError: Failed to
      // fetch`. Recording that it was a rejection at all — rather than a
      // response — is the point: it rules out every non-2xx explanation.
      fetchOutcomes.set(url, {
        transport: "rejected",
        errorName: error?.name ?? null,
        errorMessage: truncate(error?.message, 200),
        durationMs: Math.round(now() - startedAt),
      });
      throw error;
    }
  };
  observed.__t3CallableObserver = true;
  target.fetch = observed;
  observerInstalled = true;
  return true;
}

/** Test seam: forget observer/probe/emission state between cases. */
export function resetCallableDiagnostics() {
  watchedUrls.clear();
  fetchOutcomes.clear();
  observerInstalled = false;
  probeUsed = false;
  eventsEmitted = 0;
}

export function readResourceTiming(url) {
  try {
    const entries = url
      ? (globalThis.performance?.getEntriesByName?.(url, "resource") ?? [])
      : [];
    const entry = entries[entries.length - 1];
    if (!entry) return { found: false, entryCount: entries.length };
    return {
      found: true,
      entryCount: entries.length,
      // Chromium-only; null elsewhere. When present it is the most valuable
      // field in this module — the real HTTP status of a response the SDK
      // never let us see.
      responseStatus: entry.responseStatus ?? null,
      transferSize: entry.transferSize ?? null,
      encodedBodySize: entry.encodedBodySize ?? null,
      nextHopProtocol: entry.nextHopProtocol || null,
      deliveryType: entry.deliveryType || null,
      durationMs: Math.round(entry.duration ?? 0),
      // Non-zero only when the browser ran a CORS preflight for this request,
      // which is the direct read on "preflight or POST?".
      preflightMs:
        entry.requestStart && entry.startTime
          ? Math.round(entry.requestStart - entry.startTime)
          : null,
    };
  } catch {
    return { found: false, entryCount: 0 };
  }
}

/**
 * What the network stack saw, plus the one derived field worth querying on.
 *
 * `phase` is as close to "preflight or POST?" as the browser will ever let us
 * get:
 *   - `no-request`  nothing reached the network — offline, DNS, or the request
 *                   was never issued.
 *   - `blocked`     a request went out and bytes may have come back, but the
 *                   browser refused to expose the response. This is what a
 *                   rejected preflight or a missing ACAO looks like.
 *   - `response`    a real HTTP response reached JS; `status` is the truth.
 */
export function readNetworkEvidence(url) {
  const fetchOutcome = url ? (fetchOutcomes.get(url) ?? null) : null;
  const timing = readResourceTiming(url);

  let phase;
  if (fetchOutcome?.transport === "response") {
    phase = "response";
  } else if (fetchOutcome?.transport === "rejected") {
    phase = timing.found ? "blocked" : "no-request";
  } else if (timing.found) {
    phase = timing.responseStatus ? "response" : "blocked";
  } else {
    phase = "no-request";
  }

  return { phase, fetch: fetchOutcome, timing };
}

/**
 * Token age, not identity. "Was this a page that had just signed in, or one
 * running on an hour-old token?" is a live hypothesis for GH#566 and costs one
 * cached `getIdTokenResult` to answer.
 */
export async function readAuthEvidence(app) {
  try {
    const user = getAuth(app)?.currentUser;
    if (!user) return { signedIn: false };
    const result = await user.getIdTokenResult(false);
    const nowMs = Date.now();
    const ageSec = (iso) =>
      iso ? Math.round((nowMs - Date.parse(iso)) / 1000) : null;
    const authAgeSec = ageSec(result.authTime);
    return {
      signedIn: true,
      uid: user.uid,
      tokenAgeSec: ageSec(result.issuedAtTime),
      tokenExpiresInSec: result.expirationTime
        ? Math.round((Date.parse(result.expirationTime) - nowMs) / 1000)
        : null,
      authAgeSec,
      freshSignIn: authAgeSec != null && authAgeSec < 120,
      signInProvider: result.signInProvider ?? null,
    };
  } catch (error) {
    return { signedIn: null, error: truncate(error?.message, 120) };
  }
}

/**
 * Where and when.
 *
 * `deployment_id` / `commit` / `env` mirror `ENV_CONTEXT` in
 * `src/reports/server/telemetry.js` on purpose, so one Sentry query spans the
 * browser lane and the Function lane. They are injected by `vite.config.mjs`
 * from Vercel's build environment and are null on a local build.
 *
 * `pageAgeMs` is the field GH#566 turns on: the failure is reported only on the
 * FIRST page load after a fresh deploy, so a cluster of events with a small
 * `pageAgeMs` and one distinct `deployment_id` confirms or kills that theory in
 * a single query.
 */
export function readClientEvidence() {
  const nav = (() => {
    try {
      return globalThis.performance?.getEntriesByType?.("navigation")?.[0];
    } catch {
      return null;
    }
  })();
  return {
    origin: globalThis.location?.origin ?? null,
    // `|| null`, not `??`: Vite's env pipeline carries these as strings, so an
    // absent Vercel var arrives as "" rather than undefined.
    env: import.meta.env.VITE_VERCEL_ENV || import.meta.env.MODE || null,
    deployment_id: import.meta.env.VITE_DEPLOYMENT_ID || null,
    commit: import.meta.env.VITE_COMMIT_SHA || null,
    online: globalThis.navigator?.onLine ?? null,
    pageAgeMs: Math.round(now()),
    navigationType: nav?.type ?? null,
  };
}

/**
 * Replay the failed request once, reading the response the SDK discarded.
 *
 * The trade-off, stated plainly: this RE-INVOKES the callable. That is only
 * acceptable for reads — hence PROBE_ALLOWLIST — and only once per page load —
 * hence `probeUsed`, because a broken report screen fires eighteen callables
 * and must not become eighteen extra invocations.
 *
 * When the failure is CORS the probe's own fetch rejects exactly as the SDK's
 * did. That is not a wasted call, it is the persistence signal: a probe that
 * comes back 200 says the endpoint recovered on its own (the deploy-race
 * hypothesis), and a probe that rejects again says the allowlist is simply
 * wrong for this origin.
 */
async function probeEndpoint({ url, name, data, app }) {
  if (!PROBE_ALLOWLIST.has(name)) {
    return { attempted: false, reason: "not-read-only" };
  }
  if (probeUsed) return { attempted: false, reason: "already-probed" };
  if (!url) return { attempted: false, reason: "no-url" };
  probeUsed = true;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  const startedAt = now();
  try {
    const user = getAuth(app)?.currentUser;
    const token = user ? await user.getIdToken() : null;
    const response = await globalThis.fetch(url, {
      method: "POST",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ data: data ?? {} }),
    });
    return {
      attempted: true,
      ...(await summarizeResponse(response, startedAt)),
    };
  } catch (error) {
    return {
      attempted: true,
      transport: error?.name === "AbortError" ? "timeout" : "rejected",
      errorName: error?.name ?? null,
      errorMessage: truncate(error?.message, 200),
      durationMs: Math.round(now() - startedAt),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Assemble the event body. Two awaits and no side effects beyond the probe, so
 * the shape is unit-testable without a browser.
 */
export async function buildCallableFailureContext({
  functionsInstance,
  name,
  data,
  url,
  error,
  startedAt,
}) {
  const app = functionsInstance?.app;

  // BEFORE the probe, always. The probe posts to the same URL through the same
  // observed `fetch`, so it appends its own resource-timing entry and overwrites
  // the recorded fetch outcome for this URL. Reading afterwards would report the
  // probe's result as the failure's — losing the one piece of evidence the whole
  // module exists to capture.
  const network = readNetworkEvidence(url);

  const [auth, probe] = await Promise.all([
    readAuthEvidence(app),
    probeEndpoint({ url, name, data, app }),
  ]);

  return {
    request: {
      name,
      url: url ?? null,
      orgId: data?.orgId ?? null,
      // Key NAMES only, never the payload. Enough to tell a balance-sheet call
      // from a trial check without carrying one financial value.
      payloadKeys: data ? Object.keys(data).sort() : [],
      durationMs:
        typeof startedAt === "number"
          ? Math.round(Date.now() - startedAt)
          : null,
    },
    error: {
      code: error?.code ?? null,
      message: truncate(error?.message, 200),
    },
    network,
    auth,
    client: readClientEvidence(),
    probe,
  };
}

/**
 * ONE Sentry event per opaque failure, and no console output: the user still
 * gets the app's existing error handling, and the diagnosis lands where it can
 * be queried across sessions.
 */
export async function reportCallableInternalFailure(args) {
  try {
    if (eventsEmitted >= MAX_EVENTS_PER_PAGE) return null;
    eventsEmitted += 1;

    const context = await buildCallableFailureContext(args);

    captureCallableFault(args.error, {
      component: "tracedCallable",
      action: `callable/${args.name}`,
      orgId: context.request.orgId,
      tags: {
        "callable.name": args.name,
        "callable.phase": context.network.phase,
        "callable.http_status":
          context.network.fetch?.status ??
          context.network.timing.responseStatus ??
          "none",
        "callable.probe": context.probe.transport ?? context.probe.reason,
        "callable.online": context.client.online,
        "deployment.id": context.client.deployment_id ?? "unknown",
      },
      context,
    });

    return context;
  } catch {
    // Diagnostics must never replace the failure they are describing.
    return null;
  }
}
